/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: garry彭
 * @LastEditTime: 2024-04-24 12:03:58
 */
import { Table, Button, Divider, message, TablePaginationConfig } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import Status from './Status'
import { useAppDispatch, useUserId } from '../../../app/hooks'
import { fetchDoneList } from '../../../actions/invoice'
import FileSaver from 'file-saver'
import SendEmailDialog from './SendEmailDialog'

// 已开票记录类型
export interface DoneRecord {
  id: number
  index: number
  buyerName: string
  amount: number
  invoiceTypeDesc: string
  invoiceNoReal: string | null
  invoiceStatus: number
  invoiceTime: string
  realOrderNo: string | null
  invoiceUrl: string
  invoiceNo: string
  errorMessage?: string
}

// 分页状态类型
interface PaginationState {
  current: number
  defaultPageSize: number
  total?: number
}

// 组件暴露的方法
export interface DoneRef {
  refresh: () => void
}

const Done = forwardRef<DoneRef>((_, ref) => {
  const [data, setData] = useState<DoneRecord[]>([])
  const dispatch = useAppDispatch()
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    defaultPageSize: 10
  })
  const userId = useUserId()
  const [sendEmailProps, setSendEmailProps] = useState({
    outOrderNo: '',
    visible: false
  })

  const fetchData = (page: number) => {
    dispatch(
      fetchDoneList({
        userID: userId,
        page,
        pageSize: pagination.defaultPageSize
      })
    ).then((res) => {
      setData(
        res.list.map((item: Omit<DoneRecord, 'index'>, index: number) => ({
          index: (page - 1) * pagination.defaultPageSize + index + 1,
          ...item
        }))
      )

      setPagination({
        ...pagination,
        current: page,
        total: res.totalCount
      })
    })
  }

  useEffect(() => {
    fetchData(1)
  }, [])

  useImperativeHandle(
    ref,
    () => {
      return {
        refresh() {
          fetchData(pagination.current)
        }
      }
    },
    [pagination.current]
  )

  const columns: ColumnsType<DoneRecord> = [
    { title: '序号', dataIndex: 'index', width: 60 },
    { title: '发票抬头', dataIndex: 'buyerName', width: 200 },
    { title: '开票金额', dataIndex: 'amount', width: 120 },
    { title: '发票类型', dataIndex: 'invoiceTypeDesc', width: 180 },
    {
      title: '发票号码',
      dataIndex: 'invoiceNoReal',
      width: 120,
      render: (invoiceNoReal: string | null) => invoiceNoReal || '-'
    },
    {
      title: '开票状态',
      dataIndex: 'invoiceStatus',
      width: 120,
      render: (_, record) => {
        return <Status record={record} />
      }
    },
    { title: '申请时间', dataIndex: 'invoiceTime', width: 240 },
    {
      title: '订单号',
      dataIndex: 'realOrderNo',
      width: 240,
      render: (realOrderNo: string | null) => realOrderNo || '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        return (
          <>
            {record.invoiceStatus !== 5 && (
              <Button
                disabled={record.invoiceUrl === ''}
                type="link"
                style={{ padding: 0 }}
                onClick={() => {
                  FileSaver.saveAs(record.invoiceUrl)
                  message.success('已下载')
                }}
              >
                下载发票
              </Button>
            )}
            <Divider orientation="vertical" />
            {record.invoiceStatus !== 5 && (
              <Button
                disabled={record.invoiceUrl === ''}
                type="link"
                style={{ padding: 0 }}
                onClick={() => {
                  setSendEmailProps({
                    outOrderNo: record.invoiceNo,
                    visible: true
                  })
                }}
              >
                发送至邮箱
              </Button>
            )}
          </>
        )
      }
    }
  ]

  return (
    <>
      <Table<DoneRecord>
        rowKey="id"
        dataSource={data}
        columns={columns}
        pagination={pagination as TablePaginationConfig}
        onChange={({ current }) => {
          fetchData(current!)
        }}
        scroll={{
          x: columns.reduce((sum, cur) => sum + (cur.width as number || 0), 0)
        }}
      />
      <SendEmailDialog
        {...sendEmailProps}
        key={sendEmailProps.outOrderNo}
        onClose={() => {
          setSendEmailProps({
            outOrderNo: '',
            visible: false
          })
        }}
      />
    </>
  )
})

export default Done
